import styled from "@emotion/styled"
import {
	PButton,
	PInputSearch,
	PTable,
	PTableBody,
	PTableCell,
	PTableHead,
	PTableHeadCell,
	PTableHeadRow,
	PTableRow,
} from "@porsche-design-system/components-react"
import { gridGap, spacingStaticMd } from "@porsche-design-system/components-react/emotion"
import type { ExteriorColorOptionView } from "@slfinrtl/ui-bff-routes"
import { FormattedMessage, useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import type { Option } from "src/components"
import { ButtonGroup, ExteriorColorTile, Grid, GridItem, Select, SmallIconOverlay } from "src/components"
import { selectCatalogExteriorColorMessages } from "./selectCatalogExteriorColor.messages"

export interface SelectCatalogExteriorColorProps {
	exteriorColor: {
		options: ExteriorColorOptionView[]
		setValue: (exteriorColorOptionCode: string) => void
		value?: string
	}
	isSubmitting: boolean
	onSubmit: () => Promise<void>
	searchText: {
		setValue: (searchText: string) => void
		value: string
	}
	showCustomExteriorColorContainer: () => void
	tags: {
		options: Option[]
		setValue: (value: string | null) => void
		value: string | null
	}
	types: {
		options: Option[]
		setValue: (value: string | null) => void
		value: string | null
	}
}

export const SelectCatalogExteriorColor = ({
	exteriorColor,
	isSubmitting,
	onSubmit,
	searchText,
	showCustomExteriorColorContainer,
	tags,
	types,
}: SelectCatalogExteriorColorProps) => {
	const { formatMessage } = useIntl()

	return (
		<>
			<StyledGrid $rowGap={gridGap}>
				<GridItem size={{ base: 12, m: 4 }}>
					<Select
						name="colorTag"
						label={formatMessage(selectCatalogExteriorColorMessages.tag)}
						options={tags.options}
						filter
						value={tags.value}
						onChange={tags.setValue}
					/>
				</GridItem>
				<GridItem size={{ base: 12, m: 4 }}>
					<Select
						name="colorType"
						label={formatMessage(selectCatalogExteriorColorMessages.colorType)}
						options={types.options}
						filter
						value={types.value}
						onChange={types.setValue}
					/>
				</GridItem>
				<GridItem size={{ base: 12, m: 4 }}>
					<PInputSearch
						name="searchText"
						value={searchText.value}
						clear
						indicator
						onInput={(event) => searchText.setValue((event.target as HTMLInputElement).value ?? "")}
					/>
				</GridItem>
			</StyledGrid>

			<PTable caption="Select Catalog Exterior Color List">
				<PTableHead>
					<PTableHeadRow>
						<PTableHeadCell>
							<FormattedMessage {...selectCatalogExteriorColorMessages.name} />
						</PTableHeadCell>
						<PTableHeadCell>
							<FormattedMessage {...selectCatalogExteriorColorMessages.code} />
						</PTableHeadCell>
						<PTableHeadCell>
							<FormattedMessage {...selectCatalogExteriorColorMessages.colorType} />
						</PTableHeadCell>
					</PTableHeadRow>
				</PTableHead>
				<PTableBody>
					{exteriorColor.options.map((exteriorColorOption, index) => (
						<StyledPTableRow
							key={index}
							onClick={() => exteriorColor.setValue(exteriorColorOption.optionCode)}
						>
							<PTableCell>
								<SmallIconOverlay
									icon="check"
									visible={exteriorColor.value === exteriorColorOption.optionCode}
									backgroundHexCode={exteriorColorOption.hexCode}
								>
									<ExteriorColorTile
										color={exteriorColorOption.hexCode}
										label={exteriorColorOption.name}
										glossy
									/>
								</SmallIconOverlay>
							</PTableCell>
							<PTableCell>{exteriorColorOption.optionCode}</PTableCell>
							<PTableCell>{exteriorColorOption.colorType.name}</PTableCell>
						</StyledPTableRow>
					))}
				</PTableBody>
			</PTable>

			<ButtonGroup>
				<PButton loading={isSubmitting} onClick={onSubmit}>
					<FormattedMessage {...formMessages.save} />
				</PButton>
				<PButton type="button" variant="secondary" onClick={showCustomExteriorColorContainer}>
					<FormattedMessage {...selectCatalogExteriorColorMessages.customColor} />
				</PButton>
			</ButtonGroup>
		</>
	)
}

const StyledPTableRow = styled(PTableRow)`
	cursor: pointer;
`

const StyledGrid = styled(Grid)`
	align-items: flex-end;
	margin-bottom: ${spacingStaticMd};
`
